import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, AlertTriangle, Activity, IndianRupee, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";

/**
 * Downtime & Availability panel — designed to slot into the Reports page.
 * Shows fleet-wide availability %, top machines by downtime, and monthly trend.
 */
export default function DowntimeReport() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/reports/downtime", { params: { days } });
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load downtime report");
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  if (loading) return <div className="p-8 text-slate-400">Loading downtime data…</div>;
  if (!data) return null;

  const s = data.summary;
  const topMachines = data.machines.slice(0, 8).map((m) => ({
    label: m.asset_code || m.name?.slice(0, 12) || "—",
    hours: m.downtime_hours,
    availability: m.availability_pct,
    breakdowns: m.breakdown_count,
    cost: m.total_cost,
    name: m.name,
  }));

  return (
    <div className="space-y-4" data-testid="downtime-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#7C1D3A]" /> Downtime &amp; Availability
        </h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-9 rounded-md border border-slate-200 px-2 text-sm"
          data-testid="downtime-window-select"
        >
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last 12 months</option>
        </select>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Fleet Availability"
          value={`${s.fleet_availability_pct}%`}
          color={s.fleet_availability_pct >= 95 ? "emerald" : s.fleet_availability_pct >= 85 ? "amber" : "red"}
          icon={Activity}
          data-testid="kpi-fleet-availability"
        />
        <Kpi
          label="Total Downtime"
          value={`${s.total_downtime_hours}h`}
          color="slate"
          icon={Clock}
          hint="across all breakdowns in period"
        />
        <Kpi
          label="Breakdowns"
          value={s.total_breakdowns}
          color="slate"
          icon={TrendingDown}
          hint={s.open_breakdowns > 0 ? `${s.open_breakdowns} still open` : "all resolved"}
        />
        <Kpi
          label="Downtime Cost"
          value={`₹ ${s.total_cost.toLocaleString('en-IN')}`}
          color="slate"
          icon={IndianRupee}
          hint="repair spend only"
        />
      </div>

      {topMachines.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-400 text-sm">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No breakdowns recorded in this period. Fleet has been fully available. 🎉
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-heading">Top Machines by Downtime</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={topMachines} margin={{ top: 5, right: 5, left: -10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" angle={-40} textAnchor="end" tick={{ fontSize: 10 }} height={40} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded p-2 text-xs shadow">
                          <div className="font-semibold">{d.label} — {d.name}</div>
                          <div>{d.hours}h downtime</div>
                          <div>{d.breakdowns} breakdown(s)</div>
                          <div>{d.availability}% available</div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                    {topMachines.map((m) => (
                      <Cell key={m.label} fill={m.availability >= 95 ? "#10b981" : m.availability >= 85 ? "#f59e0b" : "#dc2626"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-heading">Monthly Downtime Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {data.monthly_trend.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No trend data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.monthly_trend} margin={{ top: 5, right: 5, left: -10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${v}h`} />
                    <Bar dataKey="hours" fill="#7C1D3A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Machine detail table */}
      {data.machines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-heading">Machine-Level Detail</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600 text-xs">Asset</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600 text-xs">Downtime</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600 text-xs">Breakdowns</th>
                  <th className="text-center px-4 py-2 font-semibold text-slate-600 text-xs">Availability</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600 text-xs">Repair Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.machines.map((m) => (
                  <tr key={m.asset_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-mono text-xs text-slate-500">{m.asset_code}</div>
                      <div className="text-slate-900">{m.name}</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{m.downtime_hours}h</td>
                    <td className="px-4 py-2 text-right tabular-nums">{m.breakdown_count}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        m.availability_pct >= 95 ? "bg-emerald-100 text-emerald-700" :
                        m.availability_pct >= 85 ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {m.availability_pct}%
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">₹ {m.total_cost.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="text-[11px] text-slate-500 flex items-start gap-1.5 p-2 bg-slate-50 rounded border border-slate-200">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
        Availability is calculated assuming {s.assumed_working_hours_per_month} planned working hours per machine per month (8h × 26 days).
      </div>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon, hint }) {
  const colorMap = {
    emerald: "text-emerald-700 bg-emerald-100",
    amber: "text-amber-700 bg-amber-100",
    red: "text-red-700 bg-red-100",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
          <div className="mt-1 font-heading text-xl font-bold text-slate-900">{value}</div>
          {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${colorMap[color] || colorMap.slate}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
    </div>
  );
}
